import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { User, Bot, Database, Monitor, HardDrive, Server, Lock, Key, GitBranch } from 'lucide-react';
import { SimpleGraphNode, SimpleNodeData } from './types';
import awsLogo from '../../assets/aws-logo.svg';
import gcpLogo from '../../assets/gcp-logo.svg';
import azureLogo from '../../assets/azure-logo.svg';

const cloudLogos: Record<string, string> = {
  aws: awsLogo,
  gcp: gcpLogo,
  azure: azureLogo,
};

const borderColors: Record<string, string> = {
  user: '#3b82f6',
  service_account: '#8b5cf6',
  resource: '#f59e0b',
  role: '#06b6d4',
  anomaly: '#ef4444',
  secret: '#10b981',
  key: '#f97316',
  token: '#6366f1',
};

const NodeIcon = ({ type, resourceType }: { type: string; resourceType?: string }) => {
  const iconClass = "w-5 h-5";

  if (type === 'user') return <User className={iconClass} />;
  if (type === 'service_account') return <Bot className={iconClass} />;
  if (type === 'secret') return <Lock className={iconClass} />;
  if (type === 'key') return <Key className={iconClass} />;
  if (type === 'resource') {
    if (resourceType === 'repository') return <GitBranch className={iconClass} />;
    if (resourceType === 'database') return <Database className={iconClass} />;
    if (resourceType === 'virtual-machine') return <Monitor className={iconClass} />;
    if (resourceType === 'storage-bucket') return <HardDrive className={iconClass} />;
    return <Server className={iconClass} />;
  }
  return <Server className={iconClass} />;
};

export const SimpleCardNode = memo(({ data, selected }: NodeProps<SimpleGraphNode>) => {
  const nodeData = data as SimpleNodeData;
  const borderColor = borderColors[nodeData.type] || '#6b7280';
  const provider = String(nodeData.provider || 'gcp');
  const activityPercent = nodeData.activity !== undefined ? Math.min(100, Math.max(0, nodeData.activity)) : 50;

  // Calculate activity bar color
  let activityBarColor: string;
  if (activityPercent <= 50) {
    const ratio = activityPercent / 50;
    const r = Math.round(34 + (234 - 34) * ratio);
    const g = Math.round(197 + (179 - 197) * ratio);
    const b = Math.round(94 + (8 - 94) * ratio);
    activityBarColor = `rgb(${r}, ${g}, ${b})`;
  } else {
    const ratio = (activityPercent - 50) / 50;
    const r = Math.round(234 + (239 - 234) * ratio);
    const g = Math.round(179 + (68 - 179) * ratio);
    const b = Math.round(8 + (68 - 8) * ratio);
    activityBarColor = `rgb(${r}, ${g}, ${b})`;
  }

  const typeText = nodeData.type.replace('_', ' ').toUpperCase();

  return (
    <div className="relative cursor-pointer transition-transform duration-200">
      {/* Type badge - outside the card */}
      <div
        className="absolute flex items-center gap-1.5 px-2 py-1 rounded text-white text-[11px] font-bold shadow-sm"
        style={{
          backgroundColor: borderColor,
          top: -24,
          left: 0,
        }}
      >
        <NodeIcon type={nodeData.type} resourceType={nodeData.resourceType} />
        <span>{typeText}</span>
      </div>

      {/* Main card */}
      <div
        className={`relative w-[180px] min-h-[80px] rounded-xl bg-background border-2 shadow-md p-3 transition-all ${selected ? 'ring-2 ring-sky-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]' : ''
          }`}
        style={{ borderColor }}
      >
        {/* Invisible handles */}
        <Handle
          type="target"
          position={Position.Left}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ left: '50%', top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ right: '50%', top: '50%' }}
        />

        {/* Cloud provider logo */}
        <div className="absolute top-2 right-2">
          <img
            src={cloudLogos[provider.toLowerCase()] || cloudLogos.gcp}
            alt={provider}
            className="w-5 h-5"
          />
        </div>

        {/* Content */}
        <div className="flex flex-col">
          {/* Name */}
          <span className="text-[13px] font-semibold text-foreground pr-6 leading-tight break-words">
            {nodeData.label}
          </span>

          {/* Email */}
          {nodeData.email && (
            <span className="text-[11px] text-muted-foreground leading-tight break-all mt-1">
              {nodeData.email}
            </span>
          )}

          {/* Activity bar */}
          <div className="mt-3">
            <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
              <span>Activity</span>
              <span>{Math.round(activityPercent)}%</span>
            </div>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${activityPercent}%`,
                  backgroundColor: activityBarColor
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SimpleCardNode.displayName = 'SimpleCardNode';
